/**
 * Canonical authenticated-client cleanup for WireTrack.
 * Called on logout and authenticated user identity change.
 * Does NOT clear theme or genuine UI preferences.
 */

import { clearAllListCache } from "../hooks/listQueryCache.js";
import { clearAllReferenceCache } from "../hooks/referenceDataCache.js";
import { clearScrollRestorationState } from "../hooks/useScrollRestoration.js";
import { clearAllListReturnUrls } from "../../constants/nav.js";

const LIST_SS_PREFIXES = [
  "service_",
  "kunden_",
  "projekte_",
  "maengel_",
  "offerte_",
  "inventar_",
  "archiv_",
  "pendenzen_",
  "meine_aufgaben_",
  "wt:list-return:",
  "wt:scroll-restore:",
  "adminRapportUserId",
  "leitstand_",
  "user",
  "intended",
];

/**
 * Clear sessionStorage keys that are user-specific navigation/list state.
 * Theme / prefs localStorage keys are intentionally preserved.
 */
export function clearUserScopedSessionStorage() {
  try {
    const keys = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const k = sessionStorage.key(i);
      if (k) keys.push(k);
    }
    for (const k of keys) {
      if (LIST_SS_PREFIXES.some((p) => k === p || k.startsWith(p))) {
        sessionStorage.removeItem(k);
      }
    }
  } catch {
    /* ignore */
  }
}

/**
 * Full protected-client cleanup (memory caches + nav session state).
 * Safe to call multiple times.
 */
export function clearAuthenticatedClientState() {
  try {
    clearAllListCache();
  } catch {
    /* ignore */
  }
  try {
    clearAllReferenceCache();
  } catch {
    /* ignore */
  }
  try {
    clearScrollRestorationState();
  } catch {
    /* ignore */
  }
  try {
    clearAllListReturnUrls();
  } catch {
    /* ignore */
  }
  clearUserScopedSessionStorage();
}

/**
 * @param {object|null} prevUser
 * @param {object|null} nextUser
 * @returns {boolean} true if protected caches should be cleared
 */
export function shouldClearCachesOnUserChange(prevUser, nextUser) {
  const prevId = prevUser?.id != null ? Number(prevUser.id) : null;
  const nextId = nextUser?.id != null ? Number(nextUser.id) : null;
  if (prevId == null && nextId == null) return false;
  if (prevId != null && nextId == null) return true; // logout
  if (prevId == null && nextId != null) return false; // login into empty → no prior protected data in memory needed if we cleared on logout
  if (prevId !== nextId) return true;
  const prevRole = String(prevUser?.rolle || "");
  const nextRole = String(nextUser?.rolle || "");
  const prevRoleId = prevUser?.rolle_id != null ? Number(prevUser.rolle_id) : null;
  const nextRoleId = nextUser?.rolle_id != null ? Number(nextUser.rolle_id) : null;
  if (prevRole && nextRole && prevRole !== nextRole) return true;
  if (prevRoleId != null && nextRoleId != null && prevRoleId !== nextRoleId) return true;
  return false;
}
